// Inline pill row that surfaces linked cross-resource references for the
// current chat channel / calendar event / meet room. Clicking a pill
// navigates to the linked resource so users can hop between the
// conversation, the meeting, and the event that tied them together.
//
// Usage: mount inside the header/detail area of a resource view and set
// local state `type` + `id`:
//
//   LinkedResources: { state: { type: 'chat_channel', id: root.activeChannelId } }
//
// Renders nothing when there are no links (collapses to `display: none`).

const SHELL_TYPE_META = {
  chat_channel: {
    icon: 'chat',
    label: (root, id, el) => {
      const ch = (root.channels || []).find((c) => String(c.id) === String(id))
      if (!ch) return el.call('polyglot', 'workspace.linked.conversation')
      if (ch.kind === 'channel') return '# ' + (ch.name || '')
      if (ch.kind === 'group_dm') return el.call('polyglot', 'workspace.linked.group')
      return el.call('polyglot', 'workspace.linked.dm')
    }
  },
  meet_room: {
    icon: 'video',
    label: (root, id, el) => {
      const r = (root.rooms || []).find((x) => String(x.id) === String(id))
      return r?.name || el.call('polyglot', 'workspace.linked.call')
    }
  },
  calendar_event: {
    icon: 'clock',
    label: (root, id, el) => {
      const ev = (root.events || []).find((e) => String(e.id) === String(id))
      return ev?.title || el.call('polyglot', 'workspace.linked.event')
    }
  }
}

const _shellNavigateTo = (el, type, id) => {
  const root = el.state?.root
  if (!root) return
  let rootEl = el
  while (rootEl && !rootEl.routes) rootEl = rootEl.parent
  if (type === 'chat_channel') {
    root.update({ activeChannelId: id, activeThreadRootId: null })
    if (rootEl) el.router('/chat', rootEl)
  } else if (type === 'meet_room') {
    root.update({ activeRoomId: id, meetView: 'call' })
    if (typeof window !== 'undefined') {
      window.location.hash = `room=${id}&view=call`
    }
    if (rootEl) el.router('/meet', rootEl)
  } else if (type === 'calendar_event') {
    const ev = (root.events || []).find((e) => String(e.id) === String(id))
    if (ev) el.call('openEventDetailModal', ev)
    if (rootEl) el.router('/calendar', rootEl)
  }
}

export const LinkedResources = {
  flow: 'x',
  gap: 'Z',
  align: 'center left',
  flexWrap: 'wrap',

  display: (el, s) => {
    const root = s?.root
    if (!root || !el.type || el.id == null) return 'none'
    return el.call('getLinkedResources', root, el.type, el.id).length ? 'flex' : 'none'
  },

  Label: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    text: '{{ workspace.linked.label | polyglot }}'
  },

  childExtends: 'LinkedResourcePill',
  childrenAs: 'state',
  children: (el, s) => {
    const root = s?.root
    if (!root || !el.type || el.id == null) return []
    return el.call('getLinkedResources', root, el.type, el.id)
  }
}

export const LinkedResourcePill = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center left',
  gap: 'Y',
  padding: 'Y Z',
  round: 'radiusPill',
  border: 'none',
  background: 'overlay',
  color: 'title',
  cursor: 'pointer',
  fontSize: 'Y2',
  fontWeight: '500',
  ':hover': { theme: 'dialog' },
  transition: 'background 120ms ease',

  Icon: { name: (el, s) => SHELL_TYPE_META[s.type]?.icon || 'link' },
  Text: {
    tag: 'span',
    text: (el, s) => {
      const root = el.state?.root
      const meta = SHELL_TYPE_META[s.type]
      if (!root || !meta) return ''
      return meta.label(root, s.id, el)
    }
  },

  onClick: (e, el, s) => {
    e.preventDefault()
    e.stopPropagation()
    _shellNavigateTo(el, s.type, s.id)
  }
}
